import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpService } from '../../../service/http.service';
import { GlobalService } from '../../../service/global.service';

interface StatCard {
  label: string;
  value: string;
  icon: string;
  tone: 'teal' | 'green' | 'amber' | 'violet';
  route: string;
}

@Component({
  selector: 'app-dashboard-home',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './dashboard-home.component.html',
  styleUrl: './dashboard-home.component.scss',
})
export class DashboardHomeComponent implements OnInit {
  /** Pagination size for the "recent" lists. */
  readonly PAGE = 4;

  loading = true;
  customerId: any;

  walletBalance = 0;
  ordersCount = 0;
  transactionsCount = 0;
  consultationsCount = 0;

  orders: any[] = [];
  transactions: any[] = [];

  stats: StatCard[] = [];

  constructor(private http: HttpService, public gS: GlobalService) {
    try {
      this.customerId = this.gS.userData?.id ?? this.gS.getUserDataFromStorage()?.id;
    } catch {
      this.customerId = this.gS.userData?.id;
    }
  }

  ngOnInit(): void {
    this.buildStats();
    this.load();
  }

  /**
   * Load each dashboard section independently so the page renders immediately
   * and a single slow/failing API never blocks the whole view.
   */
  load(): void {
    if (!this.customerId) {
      this.loading = false;
      return;
    }

    this.http.getUserWalletAmount(this.customerId, this.PAGE).subscribe(
      (res: any) => { this.walletBalance = res?.availableBalance ?? 0; this.buildStats(); },
      () => {},
    );

    this.http.getProductOrderHistoryNew(this.customerId, this.PAGE, 0).subscribe(
      (res: any) => {
        this.orders = res?.data ?? [];
        this.ordersCount = this.count(res, this.orders);
        this.buildStats();
      },
      () => {},
    );

    this.http.getTransactionHistory(this.customerId, this.PAGE, 0).subscribe(
      (res: any) => {
        this.transactions = res?.data ?? [];
        this.transactionsCount = this.count(res, this.transactions);
        this.buildStats();
      },
      () => {},
    );

    this.http.getChatOrderHistoryNew(this.customerId, this.PAGE, 0).subscribe(
      (res: any) => { this.consultationsCount = this.count(res, res?.data ?? []); this.buildStats(); },
      () => {},
    );

    // render the shell right away; sections fill in as their calls return
    this.loading = false;
  }

  /** Prefer a server total when present, else fall back to the fetched page length. */
  private count(res: any, list: any[]): number {
    return res?.totalCount ?? res?.totalRecords ?? res?.count ?? list.length;
  }

  private buildStats(): void {
    const cur = this.gS.currencySign || '₹';
    this.stats = [
      { label: 'Wallet Balance', value: `${cur} ${this.walletBalance || 0}`, icon: 'fa-solid fa-wallet', tone: 'teal', route: '/recharge-wallet' },
      { label: 'Product Orders', value: `${this.ordersCount}`, icon: 'fa-solid fa-box-open', tone: 'violet', route: '/customer-product-order-history' },
      { label: 'Transactions', value: `${this.transactionsCount}`, icon: 'fa-solid fa-clock-rotate-left', tone: 'green', route: '/customer-transactions-history' },
      { label: 'Consultations', value: `${this.consultationsCount}`, icon: 'fa-regular fa-comments', tone: 'amber', route: '/customer-chat-order-history' },
    ];
  }

  isCredit(txn: any): boolean {
    const t = (txn?.expenseType || txn?.transactionType || '').toString().toUpperCase();
    return t.includes('CREDIT') || t.includes('CR');
  }
}
